<template>
  <ValidationProvider tag="div" v-slot="{ errors }" :rules="rulesObject">
    <input type="hidden" :value="hiddenValue" />
    <BaseInputGroup
      :id="id + '-group'"
      :label="type !== 'checkbox' ? label : null"
      :single-column="false"
    >
      <template slot="input">
        <BaseInput
          :id="id"
          :name="cfName"
          :type="type"
          v-if="type === 'text'"
          :error="getFirstError(errors)"
          v-model="value"
          @input="customFieldUpdated"
          :aria-label="label"
        />

        <BaseInput
          :id="id"
          :name="cfName"
          :type="type === 'number' && rulesObject.integer ? 'number' : 'text'"
          v-if="type === 'number'"
          :error="getFirstError(errors)"
          v-model="value"
          @input="customFieldUpdated"
          :aria-label="label"
        />

        <BaseTextarea
          :id="id"
          :name="cfName"
          v-if="type === 'text_area'"
          :error="getFirstError(errors)"
          v-model="value"
          @input="customFieldUpdated"
          :aria-label="label"
        />

        <DatePicker
          v-if="type === 'date'"
          :id="id"
          :name="cfName"
          v-model="date"
          @change="customFieldUpdated(formatDateOrNull($event))"
          valueType="date"
          format="dddd DD. MMMM YYYY"
          :aria-label="label"
          :lang="datepickerLang"
        ></DatePicker>

        <div :class="{ 'sm:f--ml-3': singleColumn }">
          <BaseCheckbox
            :id="id"
            :name="cfName"
            v-model="value"
            v-if="type === 'checkbox'"
            @input="customFieldUpdated"
            class="f-pl-3"
          >
            <span :aria-label="label">
              {{ label }}
            </span>
          </BaseCheckbox>
        </div>
        <label>
          <select
            :id="id"
            :name="cfName"
            v-if="type === 'select_box'"
            v-model="value"
            @change="customFieldUpdated($event.target.value)"
            class="f-form-select f-mt-1 f-block f-w-full f-pl-3 f-py-1 f-text-base focus:f-outline-none f-border-gray-300 f-rounded-md f-text-gray-700"
            :aria-label="label"
          >
            <option v-for="option in customField.data.options" :key="option">
              {{ option }}
            </option>
          </select>
        </label>

        <div v-if="type === 'multi_checkbox'">
          <BaseCheckbox
            v-for="(option, index) of customField.data.options"
            :key="index"
            :name="cfName"
            :inputValue="option"
            class="f-mt-1"
            @input="multiCheckboxFieldUpdated($event, option)"
          >
            <span :aria-label="`${label}, ${option}`">
              {{ option }}
            </span>
          </BaseCheckbox>
        </div>

        <div v-if="type === 'radio'">
          <BaseRadio
            v-for="(option, index) of customField.data.options"
            :key="index"
            :name="cfName"
            :inputValue="option"
            class="f-mt-1"
            @input="customFieldUpdated(option)"
          >
            <span :aria-label="`${label}, ${option}`">
              {{ option }}
            </span>
          </BaseRadio>
        </div>
      </template>

      <template slot="error">
        <p v-if="errors.length" class="f-text-xs f-text-red-600 f-mb-0 f-mt-1">
          {{ getFirstError(errors) }}
        </p>
      </template>
    </BaseInputGroup>
  </ValidationProvider>
</template>

<script lang="ts">
import { PropType } from "vue";
import CustomFieldResource from "@/module/Form/Resources/CustomFieldResource.js";
import InputValidationMixin from "@/components/Form/Basic/Inputs/InputValidationMixin";
import { ValidationProvider } from "vee-validate";
import mixins from "vue-typed-mixins";
import BaseInputGroup from "@/components/Form/Basic/Inputs/BaseInputGroup.vue";
import BaseInput from "@/components/Form/Basic/Inputs/BaseInput.vue";
import BaseTextarea from "@/components/Form/Basic/Inputs/BaseTextarea.vue";
import BaseCheckbox from "@/components/Form/Basic/Inputs/BaseCheckbox.vue";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import DatePicker from "vue2-datepicker";
import UrlParser from "@/module/Utils/UrlParser";
import { formatDateOrNull } from "@/module/Utils/DateTimeHelpers";

interface ComponentData {
  checkboxValue: Array<any>;
  value: string | (Date & string) | null;
  date: Date | null;
  hiddenValue: any | null;
  datepickerLang: any | null;
}

export default mixins(InputValidationMixin).extend({
  name: "CustomField",

  components: {
    BaseRadio,
    BaseCheckbox,
    BaseTextarea,
    BaseInput,
    BaseInputGroup,
    ValidationProvider,
    DatePicker,
  },

  props: {
    customField: {
      type: Object as PropType<CustomFieldResource>,
      required: true,
    },
    singleColumn: {
      type: Boolean,
      required: true,
    },
  },

  data(): ComponentData {
    return {
      checkboxValue: [],
      value: null,
      date: null,
      hiddenValue: null,
      datepickerLang: null,
    };
  },

  computed: {
    id(): string {
      return this.$id(this.customField.id + "-custom-field");
    },

    label(): string {
      let label = this.customField.name || this.customField.data.name;

      if (this.isRequired && this.type !== "checkbox") {
        label += "*";
      }

      return label;
    },

    cfName(): string {
      return "custom_field_" + this.customField.id;
    },

    type(): string {
      return this.customField.data.type;
    },

    rulesObject(): { [key: string]: any } {
      const rules = {
        required: this.customField.required as
          | boolean
          | { allowFalse: boolean },
        integer: false,
        decimal: false,
        min_value: false,
      };

      if (this.type === "number") {
        rules.decimal = true;
      }

      if (this.type === "checkbox" && this.customField.required) {
        rules.required = { allowFalse: false };
      }

      if (this.customField.data.validations.length > 0) {
        for (const index in this.customField.data.validations) {
          if (!this.customField.data.validations.hasOwnProperty(index)) {
            continue;
          }

          const rule = this.customField.data.validations[index];

          if (rule.rule === "integer") {
            rules.integer = true;
          }

          if (rule.rule === "min") {
            rules.min_value = rule.value;
          }
        }
      }

      return rules;
    },

    isRequired(): boolean {
      return (
        this.rulesObject.hasOwnProperty("required") &&
        Boolean(this.rulesObject.required)
      );
    },

    finalQueryParam(): string {
      return "customField-" + this.customField.custom_field_id;
    },
  },

  methods: {
    formatDateOrNull(value: Date | null): string | null {
      return formatDateOrNull(value);
    },

    customFieldUpdated(value: any): void {
      this.hiddenValue = value;
      this.$emit("customFieldUpdated", {
        [this.customField.id]: {
          custom_field_id: this.customField.custom_field_id,
          value: value,
        },
      });
    },

    multiCheckboxFieldUpdated(value: any, option: string): void {
      const index = this.checkboxValue.findIndex((item) => item === option);

      if (value && index === -1) {
        this.checkboxValue.push(option);
      }

      if (!value && index > -1) {
        this.checkboxValue.splice(index, 1);
      }

      this.hiddenValue = this.checkboxValue;

      this.$emit("customFieldUpdated", {
        [this.customField.id]: {
          custom_field_id: this.customField.custom_field_id,
          value: this.checkboxValue,
        },
      });
    },

    getQueryParamValue(): string | null {
      const urlParser = UrlParser.getInstance();
      const value =
        urlParser.getParamFromFormData("fapi-form-" + this.finalQueryParam) ||
        urlParser.getParam("fapi-form-" + this.finalQueryParam);

      if (value === null || value === "") {
        return null;
      }

      return value;
    },
  },

  async mounted(): Promise<void> {
    if (this.customField.data.type === "date") {
      let datepickerLocale = this.$i18n.locale;

      if (datepickerLocale === "zh") {
        datepickerLocale = "en";
      }

      if (datepickerLocale === "sk") {
        const { default: skLang } = await import(
          "@/locales/datepickerTranslations/sk.js"
        );
        this.datepickerLang = skLang;
      } else {
        await import(
          /* webpackChunkName: "/locales/datepicker/[request]" */
          /* webpackInclude: /(cs|en|es|sk|de|vi|pl|ru|it|pt|fr|zh)\.js/ */
          "vue2-datepicker/locale/" + datepickerLocale
        );
      }
    }

    const paramValue = this.getQueryParamValue();

    if (this.type === "date" && paramValue !== null) {
      this.date = new Date(paramValue);

      if (this.date) {
        this.customFieldUpdated(this.date.toISOString().split("T")[0]);
      }
    } else if (this.type === "radio" && paramValue !== null) {
      const radio = this.$el.querySelector(
        '[name="' + this.cfName + '"][value="' + paramValue + '"]',
      ) as HTMLElement;

      if (radio) {
        radio.click();
      }
    } else if (this.type === "checkbox" && paramValue !== null) {
      const checkbox = this.$el.querySelector(
        '[name="' + this.cfName + '"]',
      ) as HTMLElement;

      if (checkbox && Boolean(paramValue)) {
        checkbox.click();
      }
    } else if (this.type === "multi_checkbox" && paramValue !== null) {
      const values = paramValue.split(";");

      for (const value of values) {
        const checkbox = this.$el.querySelector(
          '[name="' + this.cfName + '"][value="' + value + '"]',
        ) as HTMLElement;

        if (checkbox) {
          checkbox.click();
        }
      }
    } else if (paramValue !== null) {
      this.value = paramValue;
      this.customFieldUpdated(this.value);
    }
  },

  // mixins: [InputValidationMixin],
});
</script>

<style lang="scss">
// cannot be scoped
@import "~vue2-datepicker/index.css";

.mx-datepicker {
  width: 100% !important;
}

.mx-datepicker-popup {
  z-index: 10000000;
}

.mx-input {
  color: rgba(55, 65, 81) !important;
  border: 1px solid #d0d5db !important;
  box-shadow: inset 0 0 0 !important;
  border-radius: 0.375rem !important;
  height: 33px !important;
  &:focus {
    border-color: var(--input-color) !important;
    border-width: 2px;
  }
}
</style>
